import { useModalContext } from 'utils/contexts/modal';
import { SendingPlatform } from '../constants';
import { SmsSender } from '../types';

export enum MODAL_ACTION {
  /** ESP Accounts */
  CREATE_ACCOUNT_INTEGRATION = 'create-account-integration',
  EDIT_ACCOUNT_INTEGRATION = 'edit-account-integration',
  DISCONNECT_ACCOUNT = 'disconnect-account',

  /** Email Channels */
  CREATE_EMAIL_CHANNEL = 'create-email-channel',

  /** Default Sender */
  SET_DEFAULT_SENDER = 'set-default-sender',
  RESET_DEFAULT_SENDER = 'reset-default-sender',

  /** SMS Sender */
  ADD_SMS_SENDER = 'add-sms-sender',
  EDIT_SMS_SENDER = 'edit-sms-sender',
  DELETE_SMS_SENDER = 'delete-sms-sender',

  /** Verification Profiles */
  ADD_VERIFICATION_PROFILE = 'add-verification-profile',

  /** Destinations */
  ADD_DESTINATION = 'add-destination',
  DELETE_DESTINATION = 'delete-destination',

  /** Test Push */
  SEND_TEST_PUSH = 'send-test-push',
}

export interface ModalState {
  action: MODAL_ACTION;
  sendingPlatform?: SendingPlatform;
  espAccountId?: number;
  smsSender?: SmsSender;
}

export type ModalContextState = {
  showModal: (modalState: ModalState) => void;
  hideModal: () => void;
  modalState: ModalState | null;
};

export const useModalsContext = () => {
  const { showModal, hideModal, modalState } = useModalContext<ModalState>();

  /** ESP Account */
  const showCreateAccountModal = (sendingPlatform: SendingPlatform) =>
    showModal({
      action: MODAL_ACTION.CREATE_ACCOUNT_INTEGRATION,
      sendingPlatform,
    });
  const showEditAccountModal = (espAccountId: number, sendingPlatform?: SendingPlatform) =>
    showModal({
      action: MODAL_ACTION.EDIT_ACCOUNT_INTEGRATION,
      espAccountId,
      sendingPlatform,
    });
  const showDisconnectAccountModal = (espAccountId: number) =>
    showModal({
      action: MODAL_ACTION.DISCONNECT_ACCOUNT,
      espAccountId,
    });

  /** Default Sender */
  const showResetDefaultSenderModal = (espAccountId: number) =>
    showModal({
      action: MODAL_ACTION.RESET_DEFAULT_SENDER,
      espAccountId,
    });
  const showSetDefaultSenderModal = (espAccountId: number) =>
    showModal({
      action: MODAL_ACTION.SET_DEFAULT_SENDER,
      espAccountId,
    });

  /** SMS Sender */
  const showAddSmsSenderModal = (espAccountId: number) =>
    showModal({
      action: MODAL_ACTION.ADD_SMS_SENDER,
      espAccountId,
    });
  const showEditSmsSenderModal = (smsSender: SmsSender) =>
    showModal({
      action: MODAL_ACTION.EDIT_SMS_SENDER,
      smsSender,
    });
  const showDeleteSmsSenderModal = (smsSender: SmsSender) =>
    showModal({
      action: MODAL_ACTION.DELETE_SMS_SENDER,
      smsSender,
    });

  /** Verification Profiles */
  const showAddVerificationProfileModal = (espAccountId: number) =>
    showModal({
      action: MODAL_ACTION.ADD_VERIFICATION_PROFILE,
      espAccountId,
    });

  /** Destinations */
  const showAddDestinationModal = () =>
    showModal({
      action: MODAL_ACTION.ADD_DESTINATION,
    });
  const showDeleteDestinationModal = () =>
    showModal({
      action: MODAL_ACTION.DELETE_DESTINATION,
    });

  /** Test Push */
  const showSendTestPushModal = () =>
    showModal({
      action: MODAL_ACTION.SEND_TEST_PUSH,
    });

  return {
    showAddDestinationModal,
    showAddSmsSenderModal,
    showAddVerificationProfileModal,
    showCreateAccountModal,
    showDeleteDestinationModal,
    showDeleteSmsSenderModal,
    showDisconnectAccountModal,
    showEditAccountModal,
    showEditSmsSenderModal,
    showResetDefaultSenderModal,
    showSendTestPushModal,
    showSetDefaultSenderModal,
    hideModal,
    modalState,
  };
};
